/**
 * Perfis, permissões e escopo de dados.
 *
 * A tabela abaixo é a fonte única: o guard do backend e a interface leem daqui.
 * Esconder um botão NÃO é autorização — a verificação vale no servidor, sempre.
 */

export type RoleName = 'ADMINISTRADOR' | 'RH' | 'GESTOR' | 'COLABORADOR';

export const ROLE_LABELS: Record<RoleName, string> = {
  ADMINISTRADOR: 'Administrador',
  RH: 'RH',
  GESTOR: 'Gestor do cliente',
  COLABORADOR: 'Colaborador',
};

export const PERMISSIONS = [
  // Escala e reservas
  'escala:ver_propria',
  'escala:ver_equipe',
  'escala:ver_todas',
  'escala:editar_equipe',
  'escala:reabrir',
  'reserva:criar_individual',
  'reserva:criar_coletiva',
  'reserva:cancelar',
  'mapa:ver_disponibilidade',
  'sala:reservar',

  // Presença por QR: cada um confirma só a própria, e só a partir do QR escaneado.
  'presenca:confirmar',
  /** Anular check-in feito por QR, com motivo. */
  'presenca:anular',
  /** Disparar o lembrete de check-in a quem está escalado e ainda não confirmou. */
  'presenca:lembrete',

  // Check-in
  'checkin:enviar_relatorio',
  'checkin:ver_importacoes',
  'checkin:resolver_pendencias',
  'checkin:reprocessar_alerta',

  // Benefícios
  'beneficio:ver',
  'beneficio:calcular',
  'beneficio:aprovar',
  'beneficio:gerar_arquivo',
  'beneficio:baixar_arquivo',
  'ajuste:solicitar',
  'ajuste:aprovar',

  // Cadastros
  'colaborador:ver',
  'colaborador:editar',
  'colaborador:importar',
  'mesa:editar',
  'parametro:editar',
  'usuario:gerenciar',

  // Operação
  'email:reenviar',
  'auditoria:ver',
  'job:reprocessar',
  'integracoes:sincronizar',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * Matriz de permissões.
 *
 * GESTOR vê a disponibilidade geral de mesas (precisa disso para reservar) mas não os
 * dados de colaboradores de outras áreas — a distinção é aplicada no escopo, abaixo.
 *
 * Só GESTOR e ADMINISTRADOR alocam assentos. RH consulta a escala de todos, o mapa e
 * as operações de check-in/benefício, e pode cancelar reserva de qualquer pessoa, mas
 * não cria reserva.
 *
 * Sala de reunião é para todos os perfis: o COLABORADOR reserva e vê a grade de salas sem
 * ganhar o mapa de mesas (`mapa:ver_disponibilidade`) nem a escala de outras pessoas.
 */
export const ROLE_PERMISSIONS: Record<RoleName, readonly Permission[]> = {
  ADMINISTRADOR: [...PERMISSIONS],

  RH: [
    'escala:ver_propria',
    'escala:ver_equipe',
    'escala:ver_todas',
    'escala:reabrir',
    'reserva:cancelar',
    'mapa:ver_disponibilidade',
    'sala:reservar',
    'presenca:confirmar',
    'presenca:anular',
    'presenca:lembrete',
    'checkin:enviar_relatorio',
    'checkin:ver_importacoes',
    'checkin:resolver_pendencias',
    'checkin:reprocessar_alerta',
    'beneficio:ver',
    'beneficio:calcular',
    'beneficio:aprovar',
    'beneficio:gerar_arquivo',
    'beneficio:baixar_arquivo',
    'ajuste:solicitar',
    'ajuste:aprovar',
    'colaborador:ver',
    'colaborador:editar',
    'colaborador:importar',
    'parametro:editar',
    'auditoria:ver',
  ],

  GESTOR: [
    'escala:ver_propria',
    'escala:ver_equipe',
    'escala:editar_equipe',
    'reserva:criar_individual',
    'reserva:criar_coletiva',
    'reserva:cancelar',
    'mapa:ver_disponibilidade',
    'sala:reservar',
    'presenca:confirmar',
    'ajuste:solicitar',
    'colaborador:ver',
  ],

  COLABORADOR: ['escala:ver_propria', 'sala:reservar', 'presenca:confirmar'],
};

export function permissionsFor(roles: readonly RoleName[]): Set<Permission> {
  const result = new Set<Permission>();
  for (const role of roles) {
    for (const permission of ROLE_PERMISSIONS[role] ?? []) result.add(permission);
  }
  return result;
}

export function can(roles: readonly RoleName[], permission: Permission): boolean {
  return permissionsFor(roles).has(permission);
}

/** Visão de autoatendimento: só a própria escala, sem equipe nem casa inteira. */
export function isSelfServiceView(roles: readonly RoleName[]): boolean {
  const permissions = permissionsFor(roles);
  return (
    permissions.has('escala:ver_propria') &&
    !permissions.has('escala:ver_equipe') &&
    !permissions.has('escala:ver_todas')
  );
}

/** Quem pode gravar ou desfazer alocação de mesa. */
export function canAllocateSeats(roles: readonly RoleName[]): boolean {
  return can(roles, 'reserva:criar_individual') || can(roles, 'escala:editar_equipe');
}

/** Alcance dos dados de pessoas que o usuário pode ler. */
export type DataScope =
  | { kind: 'TODOS' }
  | { kind: 'EQUIPE'; managerEmployeeId: string }
  | { kind: 'PROPRIO'; employeeId: string }
  | { kind: 'NENHUM' };

export function resolveDataScope(
  roles: readonly RoleName[],
  employeeId: string | null,
): DataScope {
  if (roles.includes('ADMINISTRADOR') || roles.includes('RH')) return { kind: 'TODOS' };
  if (roles.includes('GESTOR') && employeeId) {
    return { kind: 'EQUIPE', managerEmployeeId: employeeId };
  }
  if (employeeId) return { kind: 'PROPRIO', employeeId };
  return { kind: 'NENHUM' };
}
